import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import type { ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

/** Heading varies by star count (KFZ-2336 AC #10.2). Only 3–5 stars carry ratings today. */
const RATING_HEADINGS: Record<number, string> = {
  3: 'Dieser Tarif bietet grundlegenden Schutz mit den wichtigsten Leistungen.',
  4: 'Dieser Tarif bietet guten Schutz mit einigen Leistungserweiterungen.',
  5: 'Dieser Tarif bietet sehr guten Schutz mit vielen Leistungen.',
};

const PREFERRED_POSITIONS: ConnectedPosition[] = [
  { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
];

@Component({
  selector: 'ui-rating-tooltip',
  templateUrl: './rating-tooltip.component.html',
  styleUrl: './rating-tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatingTooltipComponent {
  readonly ratingWert = input.required<number>();

  protected visible = signal(false);
  protected heading = computed(() => RATING_HEADINGS[Math.round(this.ratingWert())] ?? '');

  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  private trigger = viewChild.required<ElementRef<HTMLElement>>('trigger');
  private tooltipTpl = viewChild.required<TemplateRef<unknown>>('tooltipTpl');
  private overlayRef: OverlayRef | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.disposeOverlay());
  }

  show() {
    if (this.overlayRef?.hasAttached()) return;
    this.visible.set(true);

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.trigger())
      .withPositions(PREFERRED_POSITIONS)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
    this.overlayRef.attach(new TemplatePortal(this.tooltipTpl(), this.vcr));
  }

  hide() {
    this.visible.set(false);
    this.disposeOverlay();
  }

  toggle() {
    if (this.visible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  private disposeOverlay() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
